<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    size?: 'sm' | 'md' | 'lg' | 'xl';
    color?: 'primary' | 'secondary' | 'white' | 'red' | 'default';
    outline?: boolean;
    loading?: boolean;
  }>(),
  {
    size: 'md',
    color: 'default',
    outline: false,
    loading: false,
  },
);

const contextAttrs = useAttrs() as Record<string, string>;

const attrs = computed<Record<string, string>>(() =>
  contextAttrs.href ? contextAttrs : { type: 'button', ...contextAttrs },
);

const tag = computed<string>(() => (contextAttrs.href ? 'a' : 'button'));
</script>

<template>
  <component
    :is="tag"
    v-bind="attrs"
    :class="[
      'base-button text-weight-bold',
      `base-button--${props.size}`,
      `base-button--${props.color}`,
      {
        'text-body-s': props.size === 'sm' || props.size === 'md',
        'text-body': props.size === 'lg' || props.size === 'xl',
        'base-button--outline': props.outline,
        'base-button--loading': props.loading,
      },
    ]"
  >
    <span class="base-button__inner">
      <slot></slot>
    </span>

    <span
      v-if="props.loading"
      class="base-button__loader-wrap"
    >
      <svg
        class="base-button__loader"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        style="stroke: var(--color1, currentColor)"
      >
        <g
          stroke="#fff"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
        >
          <path
            d="M12 3v3M18.364 5.636l-2.12 2.121M21 12h-3M18.364 18.364l-2.12-2.121M12 21v-3M5.636 18.364l2.121-2.121M3 12h3M5.636 5.636l2.121 2.121"
          />
        </g>
      </svg>
    </span>
  </component>
</template>

<style lang="scss">
.base-button {
  $this: 'base-button';

  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  column-gap: 4px;
  color: inherit;
  text-align: center;
  text-decoration: none;
  outline: none;
  background-color: transparent;
  padding: 0 16px;
  border: none;
  border-radius: $brd-12;
  position: relative;
  transition:
    background-color $anim-primary,
    border-color $anim-primary;

  &:focus-visible {
    box-shadow:
      $shadow-btn-inset,
      0 0 0 2px #9fe8ff;
  }

  &:disabled {
    color: $color-black-500 !important;
    background-color: $color-black-200 !important;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    box-shadow: 0 0 3px 0 rgb(20 20 42 / 40%) inset;
  }

  &__inner {
    display: inline-flex;
    align-items: center;
  }

  &__loader-wrap {
    display: block;
    line-height: 0;
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__loader {
    animation: spin 2s linear infinite;
  }

  &--loading {
    pointer-events: none;

    .#{$this}__inner {
      opacity: 0;
    }
  }

  &--sm {
    min-height: 32px;
    padding: 0 16px;
    border-radius: $brd-8;

    .#{$this}__loader-wrap {
      transform: translate(-50%, -50%) scale(0.8);
    }
  }

  &--md {
    min-height: 32px;
    padding: 0 16px;

    .#{$this}__loader-wrap {
      transform: translate(-50%, -50%) scale(0.8);
    }
  }

  &--lg {
    min-height: 48px;
    padding: 0 20px;
  }

  &--xl {
    min-height: 56px;
    padding: 0 28px;
  }

  &--primary {
    color: $color-white;
    background-color: $color-primary-800;

    &:hover,
    &:focus,
    &:active {
      background-color: $color-primary-900;
    }

    &.#{$this}--outline {
      color: $color-primary-800;
      border: 1px solid $color-primary-800;
      background-color: transparent;

      &:hover,
      &:focus {
        color: $color-white;
        background-color: $color-primary-800;
        border-color: $color-primary-800;
      }

      &:active {
        color: $color-white;
        background-color: $color-primary-900;
        border-color: $color-primary-900;
      }
    }
  }

  &--secondary {
    color: $color-white;
    background-color: $color-secondary-800;

    &:hover,
    &:focus,
    &:active {
      background-color: $color-secondary-900;
    }

    &.#{$this}--outline {
      color: $color-secondary-800;
      border: 1px solid $color-secondary-800;
      background-color: transparent;

      &:hover,
      &:focus {
        color: $color-white;
        background-color: $color-secondary-800;
        border-color: $color-secondary-800;
      }

      &:active {
        color: $color-white;
        background-color: $color-secondary-900;
        border-color: $color-secondary-900;
      }
    }
  }

  &--white {
    color: $color-secondary-800;
    background-color: $color-secondary-100;

    &:hover,
    &:focus,
    &:active {
      background-color: $color-white;
    }

    &.#{$this}--outline {
      color: $color-dark-blue-200;
      border: 1px solid $color-dark-blue-200;
      background-color: transparent;

      &:hover,
      &:focus {
        color: $color-white;
        background-color: $color-secondary-800;
        border-color: $color-secondary-800;
      }

      &:active {
        color: $color-white;
        background-color: $color-secondary-900;
        border-color: $color-secondary-900;
      }
    }
  }

  &--red {
    color: $color-white;
    background-color: $color-red-500;

    &:hover,
    &:focus,
    &:active {
      background-color: $color-red-600;
    }

    &.#{$this}--outline {
      color: $color-red-500;
      border: 1px solid $color-red-500;
      background-color: transparent;

      &:hover,
      &:focus {
        color: $color-white;
        background-color: $color-red-500;
        border-color: $color-red-500;
      }

      &:active {
        color: $color-white;
        background-color: $color-red-600;
        border-color: $color-red-600;
      }
    }
  }
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
